/**
 * Утилита нормализации пользовательского контекста для workflow-правил.
 *
 * Идея:
 * * ``profile_type`` — тип профиля пользователя (``company``, ``depot``,
 *   ``customer``). Служебный ``system_console`` UI никогда не показывает
 *   и скрывает из любой scope-матрицы, поэтому возвращаем ``null``.
 * * ``permission`` — уровень доступа (``admin`` / ``user``); для
 *   ``customer`` принудительно ``user`` (admin запрещён).
 *
 * Эти два поля полностью заменяют старую «роль» — во всех местах, где
 * раньше передавали ``userRole='company'``, теперь идёт
 * ``userContext={ profileType: 'company', permission: 'user' }``.
 */

export const VALID_PROFILE_TYPES = Object.freeze(['company', 'depot', 'customer']);
export const VALID_PERMISSIONS = Object.freeze(['admin', 'user']);

/**
 * Право для матрицы статусов. Если среди кодов есть admin, он главный:
 * иначе компания с permission_code=user не видит «Отменить заказ».
 * @param {unknown} codes
 * @param {unknown} [explicit]
 * @returns {'admin'|'user'}
 */
export function primaryPermissionCode(codes, explicit) {
  const list = [];
  if (typeof explicit === 'string' && explicit.trim()) {
    list.push(explicit.trim().toLowerCase());
  }
  const raw = Array.isArray(codes) ? codes : [];
  for (const code of raw) {
    if (code == null) continue;
    const text = String(code).trim().toLowerCase();
    if (text) list.push(text);
  }
  if (list.includes('admin')) return 'admin';
  const first = list.find((code) => VALID_PERMISSIONS.includes(code));
  return first || 'user';
}

/**
 * Привести пользовательский контекст к виду ``{profileType, permission}``
 * для передачи в scope-матчер и breadcrumb.
 *
 * @param {string | null | undefined} profileTypeCode
 * @param {string | null | undefined} permissionCode
 * @returns {{ profileType: 'company'|'depot'|'customer'|null, permission: 'admin'|'user' }}
 */
export function normalizeStatusContext(profileTypeCode, permissionCode) {
  const profileType = normalizeProfileType(profileTypeCode);
  let permission =
    typeof permissionCode === 'string' && permissionCode
      ? permissionCode.toLowerCase()
      : 'user';
  if (!VALID_PERMISSIONS.includes(permission)) permission = 'user';
  // Customer никогда не admin — жёстко нормализуем.
  if (profileType === 'customer') permission = 'user';
  return { profileType, permission };
}

/**
 * Нормализация одного поля — ``profile_type_code`` → одно из
 * {'company','depot','customer', null}. ``system_console`` → null.
 */
export function normalizeProfileType(profileTypeCode) {
  if (!profileTypeCode || typeof profileTypeCode !== 'string') return null;
  const pt = profileTypeCode.toLowerCase();
  if (pt === 'service_center') return 'depot'; // legacy-совместимое имя
  if (pt === 'system_console') return null; // скрываем служебный профиль
  if (VALID_PROFILE_TYPES.includes(pt)) return pt;
  return null;
}
